
import { useTranslation } from 'react-i18next';
import { PlanLayout } from "@/components/plans/PlanLayout";
import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: i * 0.08 },
  }),
};

const BCPPage = () => {
  const { t } = useTranslation();

  const benefits = [
    { title: t('plans_detail.bcp.benefit_0_title'), desc: t('plans_detail.bcp.benefit_0_desc') },
    { title: t('plans_detail.bcp.benefit_1_title'), desc: t('plans_detail.bcp.benefit_1_desc') },
    { title: t('plans_detail.bcp.benefit_2_title'), desc: t('plans_detail.bcp.benefit_2_desc') },
  ];

  const points = [
    t('plans_detail.bcp.point_0'),
    t('plans_detail.bcp.point_1'),
    t('plans_detail.bcp.point_2'),
    t('plans_detail.bcp.point_3'),
    t('plans_detail.bcp.point_4'),
    t('plans_detail.bcp.point_5'),
    t('plans_detail.bcp.point_6'),
  ];

  return (
    <PlanLayout
      title={t('plans_detail.bcp.title')}
      subtitle={t('plans_detail.bcp.subtitle')}
      imageUrl="https://images.unsplash.com/photo-1485827404703-89b55fcc595e"
    >
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-4 mt-8 text-white">{t('plans_detail.common.overview')}</h2>
        <p className="mb-4 text-white/70 leading-relaxed">
          {t('plans_detail.bcp.overview_p1')}
        </p>
        <p className="mb-8 text-white/70 leading-relaxed">
          {t('plans_detail.bcp.overview_p2')}
        </p>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.bcp.benefits_title')}</h2>
        <motion.div
          className="space-y-4 mb-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {benefits.map((item, i) => (
            <motion.div
              key={item.title}
              custom={i}
              variants={cardVariants}
              whileHover={{ scale: 1.02 }}
              className="rounded-xl border border-white/10 bg-white/5 p-6 flex items-start gap-4"
            >
              <CheckCircle className="text-[#c9a84c] mt-1 h-6 w-6 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2 text-white">{item.title}</h3>
                <p className="text-white/65 leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.bcp.points_title')}</h2>
        <ul className="list-disc pl-6 mb-8 space-y-2 text-white/70">
          {points.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>
      </motion.section>

      <motion.section
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="border border-[#c9a84c]/30 bg-[#c9a84c]/10 p-6 rounded-xl mb-8">
          <h3 className="font-bold text-lg mb-3 text-[#c9a84c]">{t('plans_detail.common.useful_links')}</h3>
          <ul className="space-y-2">
            <li>
              <a
                href="https://www.chusho.meti.go.jp/bcp/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#c9a84c] hover:underline text-sm"
              >
                {t('plans_detail.bcp.link_0_text')}
              </a>
            </li>
            <li>
              <a
                href="https://www.bousai.go.jp/kyoiku/kigyou/keizoku/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#c9a84c] hover:underline text-sm"
              >
                {t('plans_detail.bcp.link_1_text')}
              </a>
            </li>
          </ul>
        </div>

        <div className="border border-white/10 bg-white/5 p-6 rounded-xl">
          <h3 className="font-bold text-lg mb-3 text-white">{t('plans_detail.common.contact_title')}</h3>
          <p className="mb-4 text-white/65 leading-relaxed">
            {t('plans_detail.bcp.contact_desc')}
          </p>
          <Button
            onClick={() => window.location.href = 'mailto:hori@planjoy.net?subject=BCP（事業継続計画）についてのご相談'}
            variant="outline"
            size="lg"
            className="border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10 bg-transparent"
          >
            {t('plans_detail.common.contact_button')}
          </Button>
        </div>
      </motion.section>
    </PlanLayout>
  );
};

export default BCPPage;
